import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Upload, 
  Search, 
  Trash2, 
  FileSpreadsheet, 
  Loader2, 
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from "lucide-react";
import Papa from "papaparse";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [processedRows, setProcessedRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAccount, setFilterAccount] = useState("all");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchTransactions();
    fetchAccounts();
  }, [currentPage, perPage, filterAccount]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: perPage.toString(),
      });
      if (filterAccount && filterAccount !== "all") {
        params.append("account", filterAccount);
      }
      
      const response = await axios.get(`${API}/transactions?${params}`);
      setTransactions(response.data.transactions || []);
      setTotalTransactions(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (error) {
      toast.error("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/transactions/accounts`);
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file || !file.name.endsWith(".csv")) {
      toast.error("Please upload a CSV file");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setProcessedRows(0);
    setUploadStatus("Reading CSV file...");

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const rows = results.data;
        setTotalRows(rows.length);
        setUploadStatus(`Processing ${rows.length} transactions with AI...`);

        let processed = 0;
        let successCount = 0;

        for (const row of rows) {
          try {
            await axios.post(`${API}/transactions`, {
              date: row.Date || "",
              account: row.Account || "",
              description: row.Description || "",
              debit: parseFloat(row.Debit) || 0,
              credit: parseFloat(row.Credit) || 0,
            });
            successCount++;
          } catch (error) {
            console.error("Failed to process row:", row, error);
          }

          processed++;
          setProcessedRows(processed);
          setUploadProgress(Math.round((processed / rows.length) * 100));
          setUploadStatus(`Processing transaction ${processed} of ${rows.length}...`);
        }

        setUploadStatus("Upload complete!");
        setUploadProgress(100);
        
        setTimeout(() => {
          toast.success(`Successfully uploaded ${successCount} of ${rows.length} transactions`);
          setUploadDialogOpen(false);
          setUploading(false);
          setUploadProgress(0);
          setUploadStatus("");
          setCurrentPage(1);
          fetchTransactions();
          fetchAccounts();
        }, 1000);
      },
      error: (error) => {
        toast.error("Failed to parse CSV file");
        setUploading(false);
        setUploadProgress(0);
        setUploadStatus("");
      }
    });
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/transactions/${id}`);
      toast.success("Transaction deleted");
      fetchTransactions();
    } catch (error) {
      toast.error("Failed to delete transaction");
    }
  };

  // Filter transactions by search (client-side for current page)
  const filteredTransactions = transactions.filter((t) => {
    const matchesSearch =
      searchTerm === "" ||
      t.account?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getScopeColor = (scope) => {
    switch (scope) {
      case "Scope 1":
        return "bg-amber-100 text-amber-700";
      case "Scope 2":
        return "bg-blue-100 text-blue-700";
      case "Scope 3":
        return "bg-emerald-100 text-emerald-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePerPageChange = (value) => {
    setPerPage(parseInt(value));
    setCurrentPage(1);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const startRecord = totalTransactions === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const endRecord = Math.min(currentPage * perPage, totalTransactions);

  return (
    <div className="space-y-8" data-testid="transactions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#1a2e22] font-['Outfit'] tracking-tight">
            Transactions
          </h1>
          <p className="text-[#5c6b63] mt-2">
            View and manage financial transactions with carbon estimates
          </p>
        </div>
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="btn-primary flex items-center gap-2"
              data-testid="upload-csv-btn"
            >
              <Upload className="w-4 h-4" />
              Upload CSV
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Upload Transactions</DialogTitle>
            </DialogHeader>
            
            {uploading ? (
              <div className="py-8 space-y-6">
                <div className="flex flex-col items-center gap-4">
                  {uploadProgress < 100 ? (
                    <div className="relative">
                      <div className="w-20 h-20 rounded-full border-4 border-[#e0e7e3] flex items-center justify-center">
                        <span className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">
                          {uploadProgress}%
                        </span>
                      </div>
                      <div 
                        className="absolute inset-0 rounded-full border-4 border-[#00c853] border-t-transparent animate-spin"
                        style={{ animationDuration: '1s' }}
                      />
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Progress value={uploadProgress} className="h-3" />
                  <div className="flex justify-between text-sm text-[#5c6b63]">
                    <span>{processedRows} of {totalRows} transactions</span>
                    <span>{uploadProgress}%</span>
                  </div>
                </div>

                <p className="text-center text-[#5c6b63]">
                  {uploadStatus}
                </p>

                {uploadProgress < 100 && (
                  <div className="p-3 bg-emerald-50 rounded-xl text-center">
                    <p className="text-sm text-emerald-700">
                      AI is analyzing each transaction to estimate carbon emissions
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div
                  className={`upload-zone p-8 mt-4 text-center ${
                    dragActive ? "dragging" : ""
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  data-testid="upload-dropzone"
                >
                  <FileSpreadsheet className="w-12 h-12 text-[#5c6b63] mx-auto mb-4" />
                  <p className="text-[#1a2e22] font-medium mb-2">
                    Drag & drop your CSV file here
                  </p>
                  <p className="text-sm text-[#5c6b63] mb-4">
                    or click to browse
                  </p>
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    id="file-upload"
                    onChange={(e) => handleFileUpload(e.target.files[0])}
                  />
                  <label htmlFor="file-upload">
                    <Button variant="outline" className="cursor-pointer" asChild>
                      <span>Browse Files</span>
                    </Button>
                  </label>
                </div>
                <p className="text-xs text-[#5c6b63] mt-2">
                  Expected format: Date, Account, Description, Debit, Credit
                </p>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5c6b63] z-10" />
              <Input
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 bg-[#f2f7f4]/50 border-transparent focus:border-[#1a2e22]/20 focus:bg-white"
                data-testid="search-input"
              />
            </div>
            <Select value={filterAccount} onValueChange={(val) => { setFilterAccount(val); setCurrentPage(1); }}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-account">
                <SelectValue placeholder="Filter by account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {accounts.filter(account => account && account.trim() !== "").map((account) => (
                  <SelectItem key={account} value={account}>
                    {account}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-[#1a2e22] font-['Outfit']">
            All Transactions ({totalTransactions})
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#5c6b63]">Show</span>
            <Select value={perPage.toString()} onValueChange={handlePerPageChange}>
              <SelectTrigger className="w-20 h-9" data-testid="per-page-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-[#5c6b63]">per page</span>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#1a2e22] animate-spin" />
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-12">
              <FileSpreadsheet className="w-16 h-16 text-[#c8d4cc] mx-auto mb-4" />
              <p className="text-[#5c6b63] mb-2">No transactions found</p>
              <p className="text-sm text-[#5c6b63]">
                Upload a CSV file to get started
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table className="financial-table" data-testid="transactions-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Debit (£)</TableHead>
                      <TableHead className="text-right">Credit (£)</TableHead>
                      <TableHead className="text-right">Carbon (kg)</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTransactions.map((t) => (
                      <TableRow key={t.id} data-testid={`transaction-row-${t.id}`}>
                        <TableCell className="font-mono text-sm">{t.date}</TableCell>
                        <TableCell className="font-medium">{t.account}</TableCell>
                        <TableCell className="text-[#5c6b63] max-w-xs truncate">{t.description}</TableCell>
                        <TableCell className="text-right font-mono">
                          {t.debit > 0 ? t.debit.toFixed(2) : "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {t.credit > 0 ? t.credit.toFixed(2) : "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {t.carbon_kg?.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${getScopeColor(
                              t.emission_scope
                            )}`}
                          >
                            {t.emission_scope}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(t.id)}
                            className="text-[#5c6b63] hover:text-red-600"
                            data-testid={`delete-${t.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#e0e7e3]">
                  <p className="text-sm text-[#5c6b63]">
                    Showing {startRecord} to {endRecord} of {totalTransactions} transactions
                  </p>
                  
                  <div className="flex items-center gap-1">
                    {/* First Page */}
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => goToPage(1)}
                      disabled={currentPage === 1}
                      data-testid="first-page"
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </Button>
                    
                    {/* Previous Page */}
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      data-testid="prev-page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>

                    {/* Page Numbers */}
                    {getPageNumbers().map((page, idx) => (
                      page === "..." ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-[#5c6b63]">...</span>
                      ) : (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="icon"
                          className={`h-9 w-9 ${currentPage === page ? "bg-[#1a2e22] text-white" : ""}`}
                          onClick={() => goToPage(page)}
                          data-testid={`page-${page}`}
                        >
                          {page}
                        </Button>
                      )
                    ))}

                    {/* Next Page */}
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      data-testid="next-page"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>

                    {/* Last Page */}
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => goToPage(totalPages)}
                      disabled={currentPage === totalPages}
                      data-testid="last-page"
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
